/* eslint-disable max-classes-per-file */
import React from 'react';
import DatePicker from 'react-datepicker';
import { ValidatorComponent } from 'react-material-ui-form-validator';
import { CalenderIcon, Exclamation } from 'assets/icons';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import { ErrorMessage } from 'components';
import { TE } from 'appconstants';

import atoms from 'ctsutheme/components/atoms';
import { isAfter } from 'date-fns';

const { IconButton } = atoms;

const EndAdornment = ({ onClick, name, showError }) => (
  <InputAdornment>
    <IconButton
      onClick={onClick}
      name={`ICON_${name}`}
      data-id={`ICON_${name}`}
    >
      <CalenderIcon />
    </IconButton>
    { showError && (
      <Exclamation />
    )}
  </InputAdornment>
);
const DayButton = ({
  dayNum, showError, calendarRef, onClick, date, isDisabled, ...rest
}) => (
  <button
    title={`Day - ${dayNum}`}
    type="button"
    data-id={`Day - ${dayNum}`}
    disabled={isDisabled}
    onClick={(ev) => {
      if (calendarRef.current.input) calendarRef.current.input.childNodes['0'].children[1].children['0'].focus();
      onClick();
    }}
  >
    {dayNum}
  </button>
);

const CloseButton = ({ onClick, name, calendarRef }) => (
  <div className="react-datepicker__footer">
    <button
      data-id={TE.ID + name + TE.BTN}
      title="Close Calender"
      className='btn turquoise'
      // className="react-datepicker__close-button"
      type="button"
      onClick={(ev) => {
        calendarRef.current.input.childNodes['0'].children[1].children['0'].focus();
        onClick();
      }}
    >
    Done
    </button>
  </div>
);


const InputDateTimeText = React.forwardRef(({
  keyname, title, onChange, onClick, value, disabled, showError, calendarRef, ...rest
}, ref) => (
  <TextField
    ref={ref}
    fullWidth
    onKeyUp={(ev) => {
      if (ev.key === 'Escape') {
        calendarRef.current.setOpen(false);
      }
    }}
    variant="outlined"
    value={value}
    disabled={disabled}
    onChange={onChange}
    onClick={onClick}
    error={showError}
    InputProps={{
      name:keyname,
      id: `${keyname}_simple`,
      endAdornment: (
        <EndAdornment
          name={keyname}
          onClick={onClick}
          showError={showError}
        />
      ),
      inputProps: {
        title,
       'data-id': `${TE.ID}${keyname}${TE.CLNDR}`
      },
      autoComplete: 'off'
    }}
  />
));

class MUIDatePicker extends ValidatorComponent {
  render() {
    const {
      isClearable,
      disabled,
      validators,
      name,
      maxDate,
      ...rest
    } = this.props;
    const dateProps = maxDate ? {maxDate} : {};
    const { isValid } = this.state;
    const { value } = this.props;
    const showError = (validators && validators.includes('required') && !value) || (validators && validators.length !== 0 && !isValid && !disabled);

    const ref = React.createRef();
    this.calendarRef = React.createRef();

    const setCloseCalender = () => {
      this.calendarRef.current.setOpen(false);
    };

    return (
      <>
        <DatePicker
          {...rest}
          {...dateProps}
          ref={this.calendarRef}
          validators={validators}
          customInput={(
            <InputDateTimeText
              calendarRef={this.calendarRef}
              ref={ref}
              keyname={name}
              isValid={isValid}
              showError={showError}
              {...this.props}
            />
)}
          selected={value}
          value={value}
          name={name}
          dateFormat="dd-MMM-yyyy"
          isClearable={isClearable}
          disabled={disabled}
          placeholderText="Select Date"
          shouldCloseOnSelect={false}
          renderDayContents={
            (dayNum, date) => (
              <DayButton
                dayNum={dayNum}
                name={name}
                onClick={setCloseCalender}
                calendarRef={this.calendarRef}
                date={date}
                isDisabled={isAfter(date, maxDate)}
              />
            )
          }
        >
          <CloseButton
            onClick={setCloseCalender}
            calendarRef={this.calendarRef}
            name={name}
          />
        </DatePicker>
        {this.errorText()}
      </>
    );
  }

  errorText() {
    const { isValid } = this.state;
    if (isValid) {
      return null;
    }
    return <ErrorMessage msg={this.getErrorMessage()} />;
  }
}

class MUIDateTimePicker extends ValidatorComponent {
  render() {
    const {
      isClearable,
      disabled,
      validators,
      name,
      timeIntervals = 1,
      maxDate,
      ...rest
    } = this.props;
    const dateProps = maxDate ? {maxDate} : {};
    const { isValid } = this.state;
    const { value } = this.props;
    const showError = (validators && validators.includes('required') && !value) || (validators && validators.length !== 0 && !isValid && !disabled);

    const ref = React.createRef();
    this.calendarRef = React.createRef();

    const setCloseCalender = () => {
      this.calendarRef.current.setOpen(false);
    };

    return (
      <>
        <DatePicker
          {...rest}
          {...dateProps}
          ref={this.calendarRef}
          customInput={(
            <InputDateTimeText
              calendarRef={this.calendarRef}
              ref={ref}
              keyname={name}
              isValid={isValid}
              showError={showError}
              {...this.props}
            />
)}
          selected={value || new Date()}
          isClearable={isClearable}
          disabled={disabled}
          placeholderText="Select Date & Time"
          dropdownMode="scroll"
          // minTime="12:00"
          showTimeInput
          showTimeSelect
          timeIntervals={timeIntervals}
          timeCaption="Time"
          scrollableYearDropdown
          scrollableMonthYearDropdown
          timeInputLabel="Enter Time:"
          dateFormat="dd-MMM-yyyy h:mm aa"
          renderDayContents={
            (dayNum, date) => (
              <DayButton
                name={name}
                dayNum={dayNum}
                onClick={() => null}
                calendarRef={this.calendarRef}
                date={date}
                isDisabled={maxDate ? isAfter(date, maxDate) : false}
              />
            )
          }
        >
          <CloseButton
            onClick={setCloseCalender}
            name={name}
            calendarRef={this.calendarRef}
          />
        </DatePicker>
        {this.errorText()}
      </>
    );
  }

  errorText() {
    const { isValid } = this.state;
    if (isValid) {
      return null;
    }
    return <ErrorMessage msg={this.getErrorMessage()} />;
  }
}

export { MUIDatePicker, MUIDateTimePicker };
